import { applyPlayerApiError } from '../../applyPlayerApiError'
import { BE_TO_FORM_FIELD } from '../../beFieldMap'
import { allowedBeKeys } from '../../buildPlayerSubmit'
import { BE_TO_FIELD_TITLE } from '../../fieldTitles'
import { toUpdatePayload } from '../../toUpdatePayload'
import { ADMIN_BE_TO_FIELD_TITLE, ADMIN_BE_TO_FORM_FIELD } from '../constants'
import type {
  PlayerDetailsFieldSectionKey,
  PlayerDetailsFormValues,
} from '../types'
import { getSectionFields } from './sectionFields'
import { toAdminPayload } from './toAdminPayload'

/** API error key → form field, covering Edit Player and admin fields. */
const FIELD_MAP: Record<string, string> = {
  ...BE_TO_FORM_FIELD,
  ...ADMIN_BE_TO_FORM_FIELD,
}

const TITLE_MAP: Record<string, string> = {
  ...BE_TO_FIELD_TITLE,
  ...ADMIN_BE_TO_FIELD_TITLE,
}

export interface PlayerDetailsSubmitDeps {
  playerId: string
  sectionKey: PlayerDetailsFieldSectionKey
  initialValues: PlayerDetailsFormValues
  /** PUT /players/:id with the section's changed fields. */
  update: (args: {
    playerId: string
    payload: Record<string, unknown>
  }) => Promise<unknown>
  setErrors: (errors: Record<string, string>) => void
  setTouched?: (touched: Record<string, boolean>) => void
  notifySuccess: () => void
  notifyError: (message: string) => void
  /**
   * Called instead of `notifySuccess` when the section's fields did not
   * change (no PUT) — e.g. to confirm a photo the platform saved itself.
   */
  notifyUnchanged?: () => void
  onFieldErrors?: (fields: string[]) => void
  onSuccess?: () => void
}

/**
 * Build the PUT body for ONE section: its changed fields only.
 *
 * Edit Player fields go through the shared `toUpdatePayload` (so height,
 * gender and phone are encoded exactly as the Edit Player form encodes them)
 * and are scoped to the section with the shared `allowedBeKeys`.
 */
export const buildSectionPayload = (
  values: PlayerDetailsFormValues,
  initialValues: PlayerDetailsFormValues,
  sectionKey: PlayerDetailsFieldSectionKey
): Record<string, unknown> => {
  const { profileFields, adminFields } = getSectionFields(sectionKey)
  const allowed = allowedBeKeys(profileFields)
  const profilePayload = Object.fromEntries(
    Object.entries(toUpdatePayload(values, initialValues)).filter(([beKey]) =>
      allowed.has(beKey)
    )
  )
  return {
    ...profilePayload,
    ...toAdminPayload(values, initialValues, adminFields),
  }
}

/**
 * Save one section of the Player Details form. Mirrors the shared
 * `submitPlayer`: nothing changed → no request and no toast (unless the
 * caller passes `notifyUnchanged`); API field errors
 * land inline on the matching fields; anything else falls back to a toast.
 */
export const submitPlayerDetailsSection = async (
  values: PlayerDetailsFormValues,
  deps: PlayerDetailsSubmitDeps
): Promise<void> => {
  const payload = buildSectionPayload(
    values,
    deps.initialValues,
    deps.sectionKey
  )

  try {
    if (Object.keys(payload).length > 0) {
      await deps.update({ playerId: deps.playerId, payload })
      deps.notifySuccess()
    } else {
      deps.notifyUnchanged?.()
    }
    deps.onSuccess?.()
  } catch (err: unknown) {
    applyPlayerApiError(err, {
      fieldMap: FIELD_MAP,
      titleMap: TITLE_MAP,
      setErrors: deps.setErrors,
      setTouched: deps.setTouched,
      notifyError: deps.notifyError,
      onFieldErrors: deps.onFieldErrors,
    })
  }
}
